<script setup lang="ts">
import { type Ref, onBeforeMount, ref } from "vue";

import { isValidString } from "@/Utils/String";

export interface PinCodeProps {
  length: number;
  name: string;
  hasError?: boolean;
}

const emit = defineEmits<{
  (event: "change", value: string): void;
}>();

const props = withDefaults(defineProps<PinCodeProps>(), { hasError: false });

const inputRefs = ref<Ref[]>([]);
const values = ref<string[]>([]);

const focus = (index: number) => {
  inputRefs.value[index]!.value[0].focus();
  inputRefs.value[index]!.value[0].select();
};

const onChange = (event: Event, index: number) => {
  event.preventDefault();

  const currentVal = values.value[index]!.trim().split("");

  if (currentVal.length === 0 || !isValidString(currentVal[0])) {
    values.value[index] = "";
  } else {
    values.value[index] = currentVal.pop() as string;

    if (index + 1 < props.length) {
      focus(index + 1);
    }

    emit("change", values.value.join(""));
  }
};

onBeforeMount(() => {
  for (let i = 0; i < props.length; i++) {
    inputRefs.value.push(ref());
    values.value.push("");
  }
});
</script>

<template>
  <div class="pin_code">
    <input
      v-for="(ref, index) in inputRefs"
      v-model="values[index]"
      class="pin_code__input"
      inputmode="numeric"
      type="password"
      :ref="inputRefs[index]"
      :key="`${props.name}-ref-${index}`"
      :class="{ 'pin_code__input--error': props.hasError }"
      @keyup="(e) => onChange(e, index)"
    />
  </div>
</template>

<style scoped lang="scss">
.pin_code {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: $space-4;

  &__input {
    @include border(1, solid);
    @include colors.border(content, low, true);
    @include text.modification(center);
    @include colors.bg(background, base);

    width: $space-12;
    height: $space-12;
    display: flex;
    border-radius: $space-1;
    outline: none;
    font-size: 30px;
    line-height: 30px;

    &:focus {
      @include border(1, solid);
      @include colors.border(content, high, true);
    }

    &--error {
      @include border(1, solid);
      @include colors.border(semantic, error, true);
    }
  }
}
</style>
